import React, { useState, useCallback } from 'react'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import { Modal, Input, Button, Card } from 'react-daisyui'
import { session, setSession, setStore } from '../../utils/AuthSession'
import { Navigate, useNavigate } from 'react-router-dom'
import { AiOutlinePhone, AiOutlineLock, AiOutlineMail, AiOutlineCheckCircle, AiOutlineExclamationCircle, AiOutlineLoading3Quarters } from "react-icons/ai"
import { BsShieldCheck } from "react-icons/bs"


function Login() {
    const [phone, setPhone] = useState('')
    const [token, setToken] = useState('')
    const [showTokenModal, setShowTokenModal] = useState(false)
    const [loading, setLoading] = useState(false)
    const [tokenLoading, setTokenLoading] = useState(false)
    const [messages, setMessages] = useState('')
    const [tokenMessage, setTokenMessage] = useState('')
    const [phoneError, setPhoneError] = useState('')
    const [tokenError, setTokenError] = useState('')
    const navigate = useNavigate()

    // Validation functions
    const validatePhone = (phoneNumber) => {
        if (!phoneNumber.trim()) {
            return 'Nomor telepon wajib diisi'
        }
        if (phoneNumber.length < 10) {
            return 'Nomor telepon minimal 10 digit'
        }
        return ''
    }

    const validateToken = (tokenValue) => {
        if (!tokenValue.trim()) {
            return 'Kode OTP wajib diisi'
        }
        if (tokenValue.length !== 6) {
            return 'Kode OTP harus 6 digit'
        }
        return ''
    }

    const handleSendToken = useCallback(async () => {
        // Validate phone number
        const phoneValidation = validatePhone(phone)
        if (phoneValidation) {
            setPhoneError(phoneValidation)
            return
        }

        setLoading(true)
        setPhoneError('')
        setMessages('')

        try {
            const params = {
                access: 'none',
                method: API.REQUEST_TOKEN.type,
                url: API.REQUEST_TOKEN.url,
                reqBody: {
                    lid: phone
                }
            }

            const response = await Api(params)

            if (response?.status) {
                setShowTokenModal(true)
                setMessages(response.message || 'Kode OTP telah dikirim ke nomor Anda')
                setToken('')
                setTokenMessage('')
            } else {
                setMessages(response?.message || 'Gagal mengirim kode OTP. Silakan coba lagi.')
            }
        } catch (error) {
            console.error('Error sending token:', error)
            setMessages('Terjadi kesalahan. Silakan coba lagi.')
        } finally {
            setLoading(false)
        }
    }, [phone])

    if (session()) {
        return (
            <Navigate to="/" replace={true} />
        )
    }

    const handleTokenSubmit = async () => {
        // Validate token
        const tokenValidation = validateToken(token)
        if (tokenValidation) {
            setTokenError(tokenValidation)
            return
        }

        setTokenLoading(true)
        setTokenError('')
        setTokenMessage('')

        try {
            const params = {
                access: 'none',
                method: API.LOGIN_TOKEN.type,
                url: API.LOGIN_TOKEN.url,
                reqBody: {
                    lid: phone,
                    token: token
                }
            }

            const response = await Api(params)

            if (!response?.status) {
                setTokenMessage(response?.message || 'Kode OTP tidak valid')
            } else {
                // Login successful
                setTokenMessage('Login berhasil!')

                // Set session
                setSession(response.data)

                // Get store data
                try {
                    const storeParams = {
                        access: 'auth',
                        method: API.STORE.type,
                        url: API.STORE.url,
                        reqBody: {
                            partner_id: response.data.partner_id
                        }
                    }
                    const storeResponse = await Api(storeParams)
                    if (storeResponse?.status) {
                        setStore(response.data)
                    }
                } catch (storeError) {
                    console.error('Error getting store data:', storeError)
                    // Continue with login even if store data fails
                }

                // Close modal and redirect
                setTimeout(() => {
                    setShowTokenModal(false)
                    navigate('/', { replace: true })
                }, 1000)
            }
        } catch (error) {
            console.error('Error submitting token:', error)
            setTokenMessage('Terjadi kesalahan. Silakan coba lagi.')
        } finally {
            setTokenLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md mx-auto">

                {/* Header Section */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <BsShieldCheck className="text-white text-2xl" />
                        </div>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        Selamat Datang
                    </h1>
                    <p className="text-gray-600">
                        Masuk ke akun Anda untuk melanjutkan
                    </p>
                </div>

                {/* Login Card */}
                <Card className="bg-white shadow-xl border-0 overflow-hidden">
                    <Card.Body className="p-6">

                        {/* Phone Input */}
                        <div className="mb-6">
                            <label className="label">
                                <span className="label-text font-medium text-gray-700">Nomor Telepon</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <AiOutlinePhone className="text-gray-400" />
                                </div>
                                <Input
                                    type="tel"
                                    placeholder="Masukkan nomor telepon"
                                    className={`pl-10 w-full ${phoneError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    value={phone}
                                    onChange={(e) => {
                                        setPhone(e.target.value)
                                        if (phoneError) setPhoneError('')
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSendToken()
                                        }
                                    }}
                                />
                            </div>
                            {phoneError && (
                                <div className="flex items-center mt-1 text-red-600 text-sm">
                                    <AiOutlineExclamationCircle className="mr-1" />
                                    {phoneError}
                                </div>
                            )}
                        </div>

                        {/* Send Token Button */}
                        <Button
                            onClick={handleSendToken}
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 rounded-lg mb-4"
                            size="lg"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                                    Mengirim Kode OTP...
                                </div>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <AiOutlineMail className="mr-2" />
                                    Kirim Kode OTP
                                </div>
                            )}
                        </Button>

                        {/* Messages */}
                        {messages && (
                            <div className={`text-center p-3 rounded-lg mb-4 ${
                                messages.includes('berhasil') || messages.includes('dikirim')
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : 'bg-red-50 text-red-700 border border-red-200'
                            }`}>
                                <div className="flex items-center justify-center">
                                    {messages.includes('berhasil') || messages.includes('dikirim') ? (
                                        <AiOutlineCheckCircle className="mr-2" />
                                    ) : (
                                        <AiOutlineExclamationCircle className="mr-2" />
                                    )}
                                    {messages}
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* Footer */}
                <div className="text-center mt-6">
                    <p className="text-sm text-gray-500">
                        Dengan masuk, Anda menyetujui{' '}
                        <span className="text-blue-600 cursor-pointer hover:underline">Syarat & Ketentuan</span>
                        {' '}kami
                    </p>
                </div>
            </div>

            {/* OTP Modal */}
            <Modal
                open={showTokenModal}
                onClickBackdrop={() => setShowTokenModal(false)}
                className="max-w-md mx-4 animate-in fade-in duration-200"
            >
                <Modal.Header className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-lg">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            <AiOutlineLock className="text-white text-sm" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Verifikasi OTP</h3>
                            <p className="text-blue-100 text-sm">Kode verifikasi 6 digit</p>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="px-6 py-6">
                    {/* OTP Icon and Description */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <AiOutlineMail className="text-blue-600 text-2xl" />
                        </div>
                        <h4 className="font-semibold text-gray-800 mb-2">Masukkan Kode OTP</h4>
                        <p className="text-gray-600 text-sm mb-1">
                            Kode verifikasi telah dikirim ke:
                        </p>
                        <div className="inline-flex items-center bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                            <AiOutlinePhone className="text-blue-600 mr-2" />
                            <span className="font-mono font-semibold text-blue-800">{phone}</span>
                        </div>
                    </div>

                    {/* OTP Input Section */}
                    <div className="space-y-6">
                        {/* OTP Input */}
                        <div className="space-y-4">
                            <label className="block text-center">
                                <span className="text-gray-700 font-medium">Masukkan 6 digit kode OTP</span>
                            </label>

                            {/* Hidden Input */}
                            <Input
                                type="text"
                                className="sr-only"
                                value={token}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setToken(value);
                                    if (tokenError) setTokenError('');
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && token.length === 6) {
                                        handleTokenSubmit();
                                    }
                                }}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                                    setToken(paste);
                                    if (tokenError) setTokenError('');
                                }}
                                maxLength={6}
                                autoFocus
                                inputMode="numeric"
                                pattern="[0-9]*"
                            />

                            {/* Visual OTP Boxes */}
                            <div className="flex justify-center space-x-2">
                                {[...Array(6)].map((_, i) => (
                                    <div
                                        key={i}
                                        onClick={() => {
                                            // Focus on hidden input when clicking on box
                                            const hiddenInput = document.querySelector('input[type="text"].sr-only');
                                            if (hiddenInput) hiddenInput.focus();
                                        }}
                                        className={`w-14 h-14 border-2 rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-300 ease-in-out ${
                                            i < token.length
                                                ? 'border-blue-500 bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg transform scale-110 animate-pulse'
                                                : tokenError
                                                ? 'border-red-400 bg-red-50 animate-bounce'
                                                : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50 hover:shadow-md'
                                        }`}
                                        style={{
                                            animationDelay: i < token.length ? `${i * 0.1}s` : '0s'
                                        }}
                                    >
                                        <span className="text-xl font-mono font-bold select-none">
                                            {token[i] || ''}
                                        </span>
                                        {i >= token.length && !tokenError && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="w-2 h-2 bg-gray-400 rounded-full opacity-50"></div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {tokenError && (
                                <div className="flex items-center justify-center mt-3 text-red-600 text-sm">
                                    <AiOutlineExclamationCircle className="mr-2" />
                                    {tokenError}
                                </div>
                            )}

                            {/* Progress Indicator */}
                            <div className="text-center">
                                <div className="inline-flex items-center space-x-2 text-sm">
                                    <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                                        token.length === 6 ? 'bg-green-500' : 'bg-gray-400'
                                    }`}></div>
                                    <span className={`font-medium transition-colors duration-300 ${
                                        token.length === 6 ? 'text-green-600' : 'text-gray-500'
                                    }`}>
                                        {token.length === 6 ? 'OTP lengkap!' : `${token.length} dari 6 digit`}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Status Messages */}
                        {tokenMessage && (
                            <div className={`text-center p-4 rounded-xl border-2 transition-all duration-300 ${
                                tokenMessage.includes('berhasil')
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-red-50 text-red-700 border-red-200'
                            }`}>
                                <div className="flex items-center justify-center">
                                    {tokenMessage.includes('berhasil') ? (
                                        <AiOutlineCheckCircle className="mr-2 text-xl" />
                                    ) : (
                                        <AiOutlineExclamationCircle className="mr-2 text-xl" />
                                    )}
                                    <span className="font-medium">{tokenMessage}</span>
                                </div>
                            </div>
                        )}

                        {/* Resend OTP Option */}
                        <div className="text-center">
                            <p className="text-gray-500 text-sm mb-2">
                                Tidak menerima kode?
                            </p>
                            <button
                                type="button"
                                className="text-blue-600 hover:text-blue-800 font-medium text-sm underline"
                                onClick={() => {
                                    // Reset and resend OTP
                                    setToken('');
                                    setTokenError('');
                                    setTokenMessage('');
                                    handleSendToken();
                                }}
                            >
                                Kirim ulang kode OTP
                            </button>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Actions className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
                    <Button
                        variant="outline"
                        onClick={() => setShowTokenModal(false)}
                        disabled={tokenLoading}
                        className="px-6 py-2 border-gray-300 hover:bg-gray-100"
                    >
                        Batal
                    </Button>
                    <Button
                        onClick={handleTokenSubmit}
                        disabled={tokenLoading || token.length !== 6}
                        className={`px-8 py-3 font-semibold rounded-lg transition-all duration-200 ${
                            token.length === 6 && !tokenLoading
                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {tokenLoading ? (
                            <div className="flex items-center">
                                <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                                Memverifikasi...
                            </div>
                        ) : token.length === 6 ? (
                            <div className="flex items-center">
                                <AiOutlineCheckCircle className="mr-2" />
                                Verifikasi OTP
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <AiOutlineLock className="mr-2" />
                                Masukkan OTP Lengkap
                            </div>
                        )}
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    )
}

export default Login